import { useAlerts } from "components/Alerts/AlertProvider";
import { useCallback } from "react";

/** One "set as default assignee" save: the mutation to run and its toast copy. */
export type SetDefaultAssigneeRequest = {
  /**
   * Fires the mutation and reports whether the server actually saved: false for
   * a null/absent payload, which GraphQL returns without throwing.
   */
  run: () => Promise<boolean>;
  successMessage: string;
  /** Used for both a null payload and a thrown error, so the copy can't drift. */
  errorMessage: string;
};

/**
 * THE control flow behind every "set as default assignee" button — facility,
 * region, business unit, and the staff facility default.
 *
 * All four share the same three outcomes and used to spell them out separately,
 * which is how they drifted: some checked for a null payload and some only
 * caught throws, so a save the server declined could look like it worked. The
 * call sites keep their own mutation, variables, and copy; only the sequencing
 * lives here.
 *
 * Returns a callback rather than taking the request as a hook argument, because
 * a click handler needs to run it on demand (and with per-scope copy) rather
 * than at render time.
 */
export function useSetDefaultAssignee(): (request: SetDefaultAssigneeRequest) => Promise<void> {
  const alerts = useAlerts();
  return useCallback(
    async ({ run, successMessage, errorMessage }: SetDefaultAssigneeRequest) => {
      try {
        if (!(await run())) {
          alerts.error(errorMessage);
          return;
        }
        alerts.success(successMessage);
      } catch (e) {
        alerts.error(errorMessage, e);
      }
    },
    [alerts]
  );
}
